import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../utils/api';
import './SalarySlip.css';

function SalarySlip() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [salaryData, setSalaryData] = useState([]);
  const [filteredSalary, setFilteredSalary] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Admin upload states
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [uploadType, setUploadType] = useState('single');
  const [uploadNik, setUploadNik] = useState('');
  const [uploadMonth, setUploadMonth] = useState(new Date().getMonth() + 1);
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear());
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Get current year and past 5 years for the dropdown
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
  
  // Month mapping for display
  const monthNames = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ];
  
  // Check authentication and load data
  useEffect(() => {
    const checkAuthAndLoadData = async () => {
      if (!ApiService.isAuthenticated()) {
        navigate('/');
        return;
      }

      // Get user data
      const userData = ApiService.getCurrentUser();
      setUser(userData);

      try {
        // Fetch salary slips using the user's NIK
        if (userData && userData.nik) {
          console.log("Fetching documents for NIK:", userData.nik);
          const response = await ApiService.getDocuments(userData.nik);
          console.log("API Response:", response);
          
          if (response && response.documents && response.documents.length > 0) {
            console.log("Documents from API:", response.documents);
            
            // Transform data to match component expectations
            const formattedData = response.documents.map(doc => {
              console.log("Processing document:", doc);
              
              // Extract month and year from file_name if it exists
              let month = '';
              let year = '';
              let fileName = doc.file_name || '';
              
              if (fileName) {
                console.log("Processing file_name:", fileName);
                
                // Try to extract from filename format: 1215_05_2025.pdf
                if (fileName.includes('_')) {
                  const nameParts = fileName.split('/').pop().split('_');
                  console.log("Name parts from underscore split:", nameParts);
                  
                  if (nameParts.length >= 3) {
                    month = nameParts[1];
                    // Remove file extension
                    year = nameParts[2].split('.')[0];
                    console.log("Extracted from underscore pattern - month:", month, "year:", year);
                  }
                } 
                // Pattern for directory format: ht/slipgaji/2505/
                else if (fileName.includes('/')) {
                  const pathParts = fileName.split('/');
                  console.log("Path parts:", pathParts);
                  
                  if (pathParts.length >= 3) {
                    const yearMonthPart = pathParts[2]; // e.g. "2505"
                    if (yearMonthPart.length >= 4) {
                      year = yearMonthPart.substring(0, 2); // First 2 chars for year
                      month = yearMonthPart.substring(2); // Rest for month
                      console.log("Extracted from path pattern - month:", month, "year:", year);
                    }
                  }
                }
              } 
              // Use explicit month/year if provided
              if (doc.month && doc.year) {
                month = doc.month;
                year = doc.year;
                console.log("Using explicit month/year fields:", month, year);
              }
              
              const formattedDoc = {
                id: doc.id || `${doc.nik}_${month}_${year}`,
                nik: doc.nik,
                month: month,
                year: year,
                date: `${month}/${year}`,
                status: 'Tersedia',
                fileName: fileName
              };
              
              console.log("Formatted document:", formattedDoc);
              return formattedDoc;
            });
            
            console.log("Final formatted data:", formattedData);
            setSalaryData(formattedData);
          } else {
            console.log("No documents found in response");
            setSalaryData([]);
          }
        } else {
          console.log("User data or NIK not available:", userData);
        }
      } catch (error) {
        console.error("Error fetching salary slips:", error);
        setError('Gagal memuat data slip gaji');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndLoadData();
  }, [navigate]);
  
  // Filter salary data when selection changes
  useEffect(() => {
    console.log("Filter effect triggered:");
    console.log("Current salaryData:", salaryData);
    console.log("Current filters - month:", selectedMonth, "year:", selectedYear);
    
    if (salaryData.length > 0) {
      const filtered = salaryData.filter(item => {
        console.log("Filtering item:", item);
        
        // Convert types to ensure comparison works correctly
        const itemMonth = parseInt(item.month) || 0;
        const itemYear = parseInt(item.year) || 0;
        const filterMonth = parseInt(selectedMonth) || 0;
        const filterYear = parseInt(selectedYear) || 0;
        
        console.log(`Comparing - Item month: ${itemMonth}, filter month: ${filterMonth}`);
        console.log(`Comparing - Item year: ${itemYear}, filter year: ${filterYear}`);
        
        const monthCondition = filterMonth ? itemMonth === filterMonth : true;
        const yearCondition = filterYear ? itemYear === filterYear : true;
        
        console.log(`Month condition met: ${monthCondition}, Year condition met: ${yearCondition}`);
        
        return monthCondition && yearCondition;
      });
      
      console.log("Filtered results:", filtered);
      setFilteredSalary(filtered);
    } else {
      console.log("No salary data to filter");
      setFilteredSalary([]);
    }
  }, [salaryData, selectedMonth, selectedYear]);

  const handleLogout = () => {
    // Clear authentication data
    ApiService.logout();
    
    // Add a small delay to ensure state is cleared properly
    setTimeout(() => {
      // Force redirect to login page
      window.location.href = '/';
    }, 100);
  };

  const handleViewSalarySlip = async (item) => {
    try {
      console.log("Viewing salary slip:", item);
      
      // Check if it's a local document
      if (item.local || item.dataUrl) {
        console.log("Opening local document from data URL");
        
        // If we have the dataUrl directly, use it
        if (item.dataUrl) {
          // Open in new tab
          const newTab = window.open();
          newTab.document.write(`
            <html>
              <head>
                <title>Slip Gaji - ${item.nik} ${item.month}/${item.year}</title>
                <style>
                  body { margin: 0; }
                  iframe { border: none; width: 100%; height: 100vh; }
                </style>
              </head>
              <body>
                <iframe src="${item.dataUrl}" type="application/pdf"></iframe>
              </body>
            </html>
          `);
          return;
        }
        
        // Otherwise, try to get from local storage
        const localStorageUpload = require('../utils/localStorageUpload').default;
        
        // Try to find by ID first, then by period
        let slip = localStorageUpload.getSalarySlip(item.id);
        if (!slip) {
          slip = localStorageUpload.getSalarySlipByPeriod(item.nik, item.month, item.year);
        }
        
        if (slip && slip.dataUrl) {
          // Open in new tab
          const newTab = window.open();
          newTab.document.write(`
            <html>
              <head>
                <title>Slip Gaji - ${slip.nik} ${slip.month}/${slip.year}</title>
                <style>
                  body { margin: 0; }
                  iframe { border: none; width: 100%; height: 100vh; }
                </style>
              </head>
              <body>
                <iframe src="${slip.dataUrl}" type="application/pdf"></iframe>
              </body>
            </html>
          `);
          return;
        } else {
          throw new Error('Local document not found or missing data URL');
        }
      }
      
      // Regular remote document handling
      if (item.fileName) {
        console.log("Using file_name to view:", item.fileName);
        await ApiService.viewSalarySlip(item.fileName);
      } else {
        // Fallback to constructing the filename
        console.log("Constructing filename from parts");
        const fileName = `ht/slipgaji/${item.year}${item.month}/${item.nik}_${item.month}_${item.year}.pdf`;
        console.log("Constructed filename:", fileName);
        await ApiService.viewSalarySlip(fileName);
      }
    } catch (error) {
      alert('Gagal membuka slip gaji');
      console.error(error);
    }
  };

  // Note: This function may need to be updated based on how your backend actually provides downloads
  const handleDownloadSalarySlip = async (item) => {
    try {
      console.log("Downloading salary slip:", item);
      
      // Check if it's a local document
      if (item.local || item.dataUrl) {
        console.log("Downloading local document from data URL");
        
        let dataUrl;
        
        // If we have the dataUrl directly, use it
        if (item.dataUrl) {
          dataUrl = item.dataUrl;
        } else {
          // Otherwise, try to get from local storage
          const localStorageUpload = require('../utils/localStorageUpload').default;
          
          // Try to find by ID first, then by period
          let slip = localStorageUpload.getSalarySlip(item.id);
          if (!slip) {
            slip = localStorageUpload.getSalarySlipByPeriod(item.nik, item.month, item.year);
          }
          
          if (slip && slip.dataUrl) {
            dataUrl = slip.dataUrl;
          } else {
            throw new Error('Local document not found or missing data URL');
          }
        }
        
        // Create filename
        const fileName = `slip_gaji_${item.nik}_${item.month}_${item.year}.pdf`;
        
        // Create download link
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      
      // Regular remote document handling
      let fileName;
      if (item.fileName) {
        fileName = item.fileName;
        console.log("Using file_name to download:", fileName);
      } else {
        // Fallback to constructing the filename
        fileName = `ht/slipgaji/${item.year}${item.month}/${item.nik}_${item.month}_${item.year}.pdf`;
        console.log("Constructed filename for download:", fileName);
      }
      
      // First get the URL using viewSalarySlip, then download the file directly
      const response = await ApiService.viewSalarySlip(fileName);
      console.log("View response for download:", response);
      
      if (response && response.data && response.data.url) {
        console.log("Got download URL:", response.data.url);
        // Create a link and simulate click to download
        const a = document.createElement('a');
        a.href = response.data.url;
        a.download = fileName.split('/').pop() || 'slip_gaji.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        throw new Error('Tidak dapat mendapatkan URL slip gaji');
      }
    } catch (error) {
      alert('Gagal mengunduh slip gaji');
      console.error(error);
    }
  };

  // New handler for file upload
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadFile(e.target.files[0]);
      setUploadMessage('');
      setUploadStatus('');
    }
  };

  // New handler for multiple file upload
  const handleFilesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadFiles(Array.from(e.target.files));
      setUploadMessage('');
      setUploadStatus('');
    }
  };

  // New handler for upload submission
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    
    if (uploadType === 'single') {
      if (!uploadFile) {
        setUploadMessage('Pilih file PDF untuk diunggah');
        setUploadStatus('error');
        return;
      }
      
      if (uploadFile.type !== 'application/pdf') {
        setUploadMessage('Hanya file PDF yang diperbolehkan');
        setUploadStatus('error');
        return;
      }
      
      if (!uploadNik) {
        setUploadMessage('NIK karyawan diperlukan');
        setUploadStatus('error');
        return;
      }
      
      setIsUploading(true);
      setUploadMessage('Mengunggah slip gaji...');
      setUploadStatus('');
      setUploadResult(null);
      
      try {
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('nik', uploadNik);
        formData.append('month', uploadMonth);
        formData.append('year', uploadYear);
        formData.append('updateExistingUsers', 'preserve');
        formData.append('handleDuplicates', 'skip');
        
        // Add additional metadata fields
        formData.append('filename', uploadFile.name);
        formData.append('file_type', uploadFile.type);
        formData.append('file_size', uploadFile.size);
        
        const result = await ApiService.uploadSalarySlip(formData);
        
        setUploadMessage('Slip gaji berhasil diunggah');
        setUploadStatus('success');
        setUploadResult(result);
        
        // Reset file input
        setUploadFile(null);
        document.getElementById('uploadFile').value = '';
      } catch (error) {
        console.error('Upload error:', error);
        setUploadMessage(`Gagal mengunggah: ${error.message || 'Terjadi kesalahan'}`);
        setUploadStatus('error');
      } finally {
        setIsUploading(false);
      }
    } else {
      // Multiple files upload
      if (uploadFiles.length === 0) {
        setUploadMessage('Pilih setidaknya satu file PDF untuk diunggah');
        setUploadStatus('error');
        return;
      }
      
      const nonPdfFile = uploadFiles.find(file => file.type !== 'application/pdf');
      if (nonPdfFile) {
        setUploadMessage(`File "${nonPdfFile.name}" bukan PDF. Hanya file PDF yang diperbolehkan`);
        setUploadStatus('error');
        return;
      }
      
      setIsUploading(true);
      setUploadMessage('Mengunggah beberapa file...');
      setUploadStatus('');
      setUploadResult(null);
      
      try {
        // Track results
        const results = {
          total: uploadFiles.length,
          successful: 0,
          failed: 0,
          details: []
        };
        
        // Upload each file
        for (let i = 0; i < uploadFiles.length; i++) {
          const file = uploadFiles[i];
          
          try {
            // Try to extract NIK, month, and year from filename
            // Expected format: NIK_MONTH_YEAR.pdf (e.g., 1234_05_2024.pdf)
            let fileNik = '';
            let fileMonth = '';
            let fileYear = '';
            
            const nameParts = file.name.split('_');
            if (nameParts.length >= 3) {
              fileNik = nameParts[0];
              fileMonth = nameParts[1];
              fileYear = nameParts[2].split('.')[0];
            } else {
              // If filename doesn't follow the pattern, try to extract NIK from the filename
              // For files named just with NIK like "10016.pdf"
              const fileName = file.name.split('.')[0];
              // Check if the file name could be a NIK (numeric)
              if (/^\d+$/.test(fileName)) {
                fileNik = fileName;
                fileMonth = uploadMonth;
                fileYear = uploadYear;
              } else {
                // If not numeric, use the form values
                fileNik = uploadNik;
                fileMonth = uploadMonth;
                fileYear = uploadYear;
              }
            }
            
            // For multiple uploads without clear NIK, we need user-provided NIK
            if (!fileNik && !uploadNik) {
              results.failed++;
              results.details.push({
                file: file.name,
                status: 'error',
                message: 'NIK tidak dapat diekstrak dari nama file dan tidak disediakan'
              });
              continue;
            }
            
            // Use extracted NIK or fallback to provided NIK
            const nikToUse = fileNik || uploadNik;
            
            const formData = new FormData();
            formData.append('file', file);
            formData.append('nik', nikToUse);
            formData.append('month', fileMonth);
            formData.append('year', fileYear);
            formData.append('updateExistingUsers', 'preserve');
            formData.append('handleDuplicates', 'skip');
            
            // Add additional metadata fields to help server processing
            formData.append('filename', file.name);
            formData.append('file_type', file.type);
            formData.append('file_size', file.size);
            formData.append('content_type', 'application/pdf');
            
            // Update progress message
            setUploadMessage(`Mengunggah file ${i + 1}/${uploadFiles.length}: ${file.name}...`);
            
            const result = await ApiService.uploadSalarySlip(formData);
            
            results.successful++;
            results.details.push({
              file: file.name,
              status: 'success',
              nik: nikToUse,
              month: fileMonth,
              year: fileYear
            });
          } catch (error) {
            results.failed++;
            results.details.push({
              file: file.name,
              status: 'error',
              message: error.message || 'Gagal mengunggah file'
            });
          }
          
          // Add a small delay between uploads to avoid overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        setUploadMessage(`Selesai: ${results.successful} berhasil, ${results.failed} gagal dari total ${results.total} file`);
        setUploadStatus(results.failed === 0 ? 'success' : 'warning');
        setUploadResult(results);
        
        // Reset file input
        setUploadFiles([]);
        document.getElementById('uploadFiles').value = '';
        
        // Refresh salary data if needed
        if (user && results.successful > 0) {
          try {
            const response = await ApiService.getDocuments(user.nik);
            if (response && response.documents) {
              const formattedData = response.documents.map(doc => ({
                id: doc.id || `${doc.nik}_${doc.month}_${doc.year}`,
                nik: doc.nik,
                month: doc.month || '',
                year: doc.year || '',
                date: `${doc.month || ''}/${doc.year || ''}`,
                status: 'Tersedia',
                fileName: doc.file_name || ''
              }));
              setSalaryData(formattedData);
            }
          } catch (refreshError) {
            console.error('Error refreshing salary data:', refreshError);
          }
        }
      } catch (error) {
        console.error('Upload error:', error);
        setUploadMessage(`Gagal mengunggah: ${error.message || 'Terjadi kesalahan'}`);
        setUploadStatus('error');
      } finally {
        setIsUploading(false);
      }
    }
  };

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="salary-page">
      <header className="dashboard-header">
        <div className="container header-container">
          <div className="logo">
            <img src="/logo.png" alt="Company Logo" />
            <h1>Sistem Penggajian</h1>
          </div>
          <nav className="main-nav">
            <ul>
              <li><a href="/dashboard">Dashboard</a></li>
              <li className="active"><a href="/salary">Slip Gaji</a></li>
              <li><a href="/change-password">Ubah Password</a></li>
              {user && user.isAdmin && (
                <>
                  <li className="admin-menu">
                    <a href="/admin">Admin Panel</a>
                    <div className="admin-submenu">
                      <a href="/admin?tab=users">Manajemen User</a>
                      <a href="/admin?tab=upload" className="highlight-upload">⬆️ Unggah Slip Gaji</a>
                    </div>
                  </li>
                </>
              )}
            </ul>
          </nav>
          <div className="user-menu">
            <span className="username">
              {user ? (
                <>
                  {user.name || user.nik} 
                  <span className="user-role">({user.isAdmin ? 'admin' : 'user'})</span>
                </>
              ) : (
                'User'
              )}
            </span>
            <button 
              className="logout-btn"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Admin Upload Quick Access */}
      {user && user.isAdmin && (
        <div className="admin-upload-banner">
          <div className="container">
            <div className="upload-banner-content">
              <h2>Admin Area: Upload Slip Gaji</h2>
              <p>Anda memiliki akses admin untuk mengunggah slip gaji karyawan.</p>
              <button 
                className="admin-upload-banner-btn"
                onClick={() => setShowUploadPanel(!showUploadPanel)}
              >
                {showUploadPanel ? '❌ Tutup Form Upload' : '⬆️ Upload Slip Gaji'}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="salary-content">
        <div className="container">
          <div className="salary-panel">
            <div className="panel-header">
              <h2>Slip Gaji</h2>
              <div className="panel-actions">
                {user && user.isAdmin && (
                  <button 
                    className="btn-upload-slip"
                    onClick={() => setShowUploadPanel(!showUploadPanel)}
                  >
                    {showUploadPanel ? '❌ Tutup Form Unggah' : '⬆️ Unggah Slip Gaji'}
                  </button>
                )}
                <div className="filter-section">
                  <div className="filter-group">
                    <label htmlFor="month">Bulan:</label>
                    <select 
                      id="month" 
                      className="filter-select"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                      <option value="">Semua</option>
                      {monthNames.map((name, index) => (
                        <option key={index+1} value={index+1}>{name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label htmlFor="year">Tahun:</label>
                    <select 
                      id="year" 
                      className="filter-select"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    >
                      <option value="">Semua</option>
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            
            {error && <div className="alert alert-danger">{error}</div>}
            
            {/* Admin Upload Panel */}
            {user && user.isAdmin && showUploadPanel && (
              <div className="admin-upload-panel">
                <h3>Unggah Slip Gaji Baru</h3>
                <form onSubmit={handleUploadSubmit}>
                  <div className="upload-form-content">
                    <div className="form-group">
                      <label htmlFor="uploadType">Tipe Unggahan:</label>
                      <select 
                        id="uploadType" 
                        className="form-control"
                        value={uploadType}
                        onChange={(e) => setUploadType(e.target.value)}
                      >
                        <option value="single">File Tunggal</option>
                        <option value="multiple">Beberapa File</option>
                      </select>
                    </div>
                    
                    {uploadType === 'single' ? (
                      <>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="uploadNik">NIK Karyawan:</label>
                            <input 
                              type="text" 
                              id="uploadNik" 
                              className="form-control"
                              value={uploadNik}
                              onChange={(e) => setUploadNik(e.target.value)}
                              required
                            />
                          </div>
                          
                          <div className="form-group">
                            <label htmlFor="uploadMonth">Bulan:</label>
                            <select 
                              id="uploadMonth" 
                              className="form-control"
                              value={uploadMonth}
                              onChange={(e) => setUploadMonth(e.target.value)}
                            >
                              {monthNames.map((name, index) => (
                                <option key={index+1} value={index+1}>{name} ({String(index+1).padStart(2, '0')})</option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="form-group">
                            <label htmlFor="uploadYear">Tahun:</label>
                            <select 
                              id="uploadYear" 
                              className="form-control"
                              value={uploadYear}
                              onChange={(e) => setUploadYear(e.target.value)}
                            >
                              {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="uploadFile">File Slip Gaji (PDF):</label>
                          <input 
                            type="file" 
                            id="uploadFile" 
                            className="form-control"
                            accept=".pdf"
                            onChange={handleFileChange}
                            required
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="uploadMonth"><strong>Pilih Bulan untuk semua file:</strong></label>
                            <select 
                              id="uploadMonth" 
                              className="form-control"
                              value={uploadMonth}
                              onChange={(e) => setUploadMonth(e.target.value)}
                            >
                              {monthNames.map((name, index) => (
                                <option key={index+1} value={index+1}>{name} ({String(index+1).padStart(2, '0')})</option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="form-group">
                            <label htmlFor="uploadYear"><strong>Pilih Tahun untuk semua file:</strong></label>
                            <select 
                              id="uploadYear" 
                              className="form-control"
                              value={uploadYear}
                              onChange={(e) => setUploadYear(e.target.value)}
                            >
                              {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="uploadNik">NIK Karyawan (opsional, jika file tidak sesuai format):</label>
                          <input 
                            type="text" 
                            id="uploadNik" 
                            className="form-control"
                            value={uploadNik}
                            onChange={(e) => setUploadNik(e.target.value)}
                            placeholder="Gunakan jika nama file tidak berisi NIK"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="uploadFiles">
                            <strong>Pilih Beberapa File Slip Gaji (PDF):</strong>
                          </label>
                          <input 
                            type="file" 
                            id="uploadFiles" 
                            className="form-control"
                            accept=".pdf"
                            multiple
                            onChange={handleFilesChange}
                            required
                          />
                          <small className="form-text text-muted">
                            Format nama file dapat berupa:
                            <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                              <li>NIK_BULAN_TAHUN.pdf (contoh: 1234_05_2024.pdf) - bulan dan tahun dari nama file akan digunakan</li>
                              <li>NIK.pdf (contoh: 10016.pdf) - akan menggunakan bulan dan tahun yang dipilih di atas</li>
                            </ul>
                          </small>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {uploadMessage && (
                    <div className={`alert ${uploadStatus === 'success' ? 'alert-success' : uploadStatus === 'error' ? 'alert-danger' : 'alert-info'}`}>
                      {uploadMessage}
                    </div>
                  )}
                  
                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="btn-primary"
                      disabled={isUploading}
                    >
                      {isUploading ? 'Mengunggah...' : 'Unggah Slip Gaji'}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setUploadFile(null);
                        setUploadFiles([]);
                        setUploadMessage('');
                        setUploadStatus('');
                        setUploadResult(null);
                        
                        // Reset file inputs
                        const singleFileInput = document.getElementById('uploadFile');
                        const multipleFileInput = document.getElementById('uploadFiles');
                        if (singleFileInput) singleFileInput.value = '';
                        if (multipleFileInput) multipleFileInput.value = '';
                      }}
                      disabled={isUploading}
                    >
                      Reset
                    </button>
                  </div>
                </form>
                
                {uploadStatus === 'success' && uploadType === 'single' && uploadResult && (
                  <div className="upload-result">
                    <h4>Slip Gaji Berhasil Diunggah</h4>
                    <div className="result-details">
                      <p><strong>NIK:</strong> {uploadResult.nik || uploadNik}</p>
                      <p><strong>Bulan/Tahun:</strong> {uploadResult.month || uploadMonth}/{uploadResult.year || uploadYear}</p>
                      {uploadResult.path && <p><strong>File Path:</strong> {uploadResult.path}</p>}
                      
                      {uploadResult.user_created && (
                        <div className="user-created-info">
                          <p><strong>User Baru Dibuat:</strong></p>
                          <p>NIK: {uploadResult.nik}</p>
                          <p>Password awal: {uploadResult.nik}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {uploadStatus !== '' && uploadType === 'multiple' && uploadResult && (
                  <div className="upload-result">
                    <h4>Hasil Unggah Multiple</h4>
                    <div className="result-summary">
                      <p>Total: {uploadResult.total} file</p>
                      <p className="success-text">Berhasil: {uploadResult.successful} file</p>
                      {uploadResult.failed > 0 && (
                        <p className="error-text">Gagal: {uploadResult.failed} file</p>
                      )}
                    </div>
                    
                    {uploadResult.details && uploadResult.details.length > 0 && (
                      <div className="result-details">
                        <h5>Detail:</h5>
                        <ul className="upload-details-list">
                          {uploadResult.details.map((detail, index) => (
                            <li 
                              key={index} 
                              className={`upload-detail-item ${detail.status === 'success' ? 'success' : 'error'}`}
                            >
                              <div className="detail-file">{detail.file}</div>
                              {detail.status === 'success' ? (
                                <div className="detail-info">
                                  <span className="success-badge">Berhasil</span> NIK: {detail.nik}, Bulan/Tahun: {detail.month}/{detail.year}
                                </div>
                              ) : (
                                <div className="detail-error">{detail.message}</div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Bulan</th>
                    <th>Tahun</th>
                    <th>Tanggal</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSalary.length > 0 ? (
                    filteredSalary.map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>{monthNames[parseInt(item.month)-1]}</td>
                        <td>{item.year}</td>
                        <td>{`${monthNames[parseInt(item.month)-1]} ${item.year}`}</td>
                        <td>
                          <span className="status-badge active">{item.status}</span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="action-btn view"
                              onClick={() => handleViewSalarySlip(item)}
                            >
                              Lihat
                            </button>
                            <button 
                              className="action-btn download"
                              onClick={() => handleDownloadSalarySlip(item)}
                            >
                              Unduh
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="no-data">Tidak ada data slip gaji untuk periode yang dipilih</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            <div className="salary-info">
              <div className="info-card">
                <h3>Informasi Slip Gaji</h3>
                <ul>
                  <li>Slip gaji tersedia dalam format PDF</li>
                  <li>Tanyakan ke bagian HRD jika slip gaji tidak tersedia</li>
                  <li>Slip gaji dikeluarkan maksimal tanggal 30 setiap bulan</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="dashboard-footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} Sistem Penggajian. All rights reserved.</p>
        </div>
      </footer>

      {user && user.isAdmin && (
        <div className="admin-floating-menu">
          <div className="admin-floating-button">
            <span>Admin</span>
          </div>
          <div className="admin-floating-submenu">
            <a href="/admin">Dashboard Admin</a>
            <a href="/admin?tab=users">Manajemen User</a>
            <a href="/admin?tab=upload" className="highlight-upload">⬆️ Unggah Slip Gaji</a>
            <a href="#" onClick={(e) => {
              e.preventDefault(); 
              setShowUploadPanel(!showUploadPanel);
              window.scrollTo({top: 0, behavior: 'smooth'});
            }} className="highlight-upload">
              {showUploadPanel ? '❌ Tutup Form Unggah' : '⬆️ Unggah di Halaman Ini'}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalarySlip; 